<template>
  <FormularioCadastro
    module-name="Cadastro de Moedas"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="salvar"
    @cancelar="handleCancel"
  >
    <!-- Frame 1: Identificação da Moeda -->
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.identificacao = !accordionState.identificacao"
      >
        <h5 class="mb-0"><i class="bi bi-currency-exchange me-2 text-primary"></i> Identificação da Moeda</h5>
        <i :class="['bi', accordionState.identificacao ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.identificacao">
        <div class="row g-3">
          <div class="col-md-3">
            <CampoInput
              id="codigoISO"
              label="Código ISO"
              v-model="item.codigoISO"
              maxlength="3"
              placeholder="Ex: USD"
              descricao-informativa="Código ISO 4217, 3 caracteres"
              :error="errors.codigoISO"
              required
            />
          </div>

          <div class="col-md-5">
            <CampoInput
              id="nome"
              label="Nome da Moeda"
              v-model="item.nome"
              placeholder="Ex: Dólar Americano"
              :error="errors.nome"
              required
            />
          </div>

          <div class="col-md-2">
            <CampoSelect
              id="tipoMoeda"
              label="Tipo de Cotação"
              v-model="item.tipoMoeda"
              :options="opcoesTipoMoeda"
              option-value="id"
              option-label="label"
              :error="errors.tipoMoeda"
              required
            />
          </div>

          <div class="col-md-2">
            <CampoSelect
              id="lstStatus"
              label="Status"
              v-model="item.lstStatus"
              :options="listas.LstStatus || []"
              option-label="textoLista"
              option-value="idListaSistemaItem"
              :error="errors.lstStatus"
              required
            />
          </div>

          <div class="col-md-6">
            <CampoBuscaLista
              id="lstFonteCotacao"
              label="Fonte de Cotação"
              v-model="item.lstFonteCotacao"
              tabela="Moeda"
              :error="errors.lstFonteCotacao"
              required
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Frame 2: Série SGS (apenas para fonte SGS_BCB) -->
    <div class="card mb-4" v-if="isSGSVisivel">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.serieSGS = !accordionState.serieSGS"
      >
        <h5 class="mb-0"><i class="bi bi-gear me-2 text-primary"></i> Configuração Série SGS</h5>
        <i :class="['bi', accordionState.serieSGS ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.serieSGS">
        <div class="row g-3">
          <div class="col-md-6">
            <CampoInput
              id="codigoSerieSGSCompra"
              label="Código Série SGS (Compra)"
              v-model="item.codigoSerieSGSCompra"
              placeholder="Ex: 10813"
              :error="errors.codigoSerieSGSCompra"
            />
          </div>

          <div class="col-md-6">
            <CampoInput
              id="codigoSerieSGSVenda"
              label="Código Série SGS (Venda)"
              v-model="item.codigoSerieSGSVenda"
              placeholder="Ex: 10814"
              :error="errors.codigoSerieSGSVenda"
            />
          </div>
        </div>
        <small class="text-muted mt-2 d-block">
          <i class="bi bi-info-circle me-1"></i>
          IDs das séries SGS do Banco Central do Brasil para consulta automática de taxas de câmbio.
        </small>
      </div>
    </div>

  </FormularioCadastro>
</template>

<script setup>
import { reactive, computed, watch, onMounted } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idMoeda: 0,
      codigoISO: '',
      nome: '',
      tipoMoeda: 'A',
      isCotacaoDireta: false,
      lstFonteCotacao: '',
      codigoSerieSGSCompra: null,
      codigoSerieSGSVenda: null,
      lstStatus: ''
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'changesDetected', 'show-notification'])

const { item, isLoading, isEditing, entityInfo, errors, submitForm, handleCancel } = useFormulario(props, emit)

const accordionState = reactive({
  identificacao: true,
  serieSGS: false
})

// Hardcoded conforme requisito: opções "A" e "B" diretamente no código
const opcoesTipoMoeda = [
  { id: 'A', label: 'Cotação Indireta' },
  { id: 'B', label: 'Cotação Direta' }
]

const listas = reactive({
  LstStatus: []
})

// Verifica se a fonte selecionada é SGS_BCB para exibir campos de série SGS
const isSGSVisivel = computed(() => {
  if (!item.value.lstFonteCotacao) return false
  const fonteStr = String(item.value.lstFonteCotacao)
  // Aceita tanto o código completo MOE_FONTE_COTACAO2 quanto o valor textual SGS
  return fonteStr === 'MOE_FONTE_COTACAO2' || fonteStr.toUpperCase().includes('SGS')
})

// Atualiza isCotacaoDireta automaticamente quando tipoMoeda muda
watch(() => item.value.tipoMoeda, (novoTipo) => {
  item.value.isCotacaoDireta = novoTipo === 'B'
})

// Se a fonte não for SGS, limpa os campos de série SGS
watch(() => item.value.lstFonteCotacao, (novaFonte) => {
  if (novaFonte && !isSGSVisivel.value) {
    item.value.codigoSerieSGSCompra = null
    item.value.codigoSerieSGSVenda = null
  }
})

const carregarListas = async () => {
  try {
    const resSt = await api.get('/ListasSistema/lookup/ST')
    listas.LstStatus = resSt.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar listas auxiliares.', type: 'error' })
  }
}

async function salvar () {
  const payload = {
    ...item.value
  }

  await submitForm('Moedas', 'idMoeda', payload)
}

onMounted(() => {
  carregarListas()
})
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
